import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, ClockCounterClockwise } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { pts, tzs, fmtDateTime } from "@/lib/format";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Pager } from "@/components/Pager";

const types = ["all", "purchase", "redemption", "adjustment"];

export default function AdminTransactions() {
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [type, setType] = useState("all");
  const [audit, setAudit] = useState(null);
  const [adj, setAdj] = useState(null);
  const [adjForm, setAdjForm] = useState({ delta: 0, reason: "" });
  const PAGE = 10;

  const load = () => api.get("/admin/transactions", { params: { ...(type !== "all" ? { type } : {}), limit: PAGE, offset: page * PAGE } }).then((r) => { setList(r.data.items); setTotal(r.data.total); }).catch(() => setList([]));
  useEffect(() => { load(); }, [type, page]); // eslint-disable-line
  useEffect(() => { setPage(0); }, [type]);

  const showAudit = async (t) => {
    try { const { data } = await api.get("/admin/audit-logs", { params: { entity_id: t.id } }); setAudit({ txn: t, logs: data }); }
    catch { toast.error("Failed"); }
  };

  const submitAdj = async () => {
    try {
      await api.post("/admin/adjustments", { business_id: adj.business_id, customer_id: adj.customer_id, delta: Number(adjForm.delta), reason: adjForm.reason, original_transaction_id: adj.id });
      toast.success("Adjustment created");
      setAdj(null); setAdjForm({ delta: 0, reason: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><Receipt size={26} className="text-primary" /> Transactions</h1>
        <Select value={type} onValueChange={setType}><SelectTrigger data-testid="admin-txn-filter" className="w-44 bg-muted/40 border-black/10 rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{types.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="text-muted-foreground text-xs uppercase tracking-wider border-b border-black/8"><tr><th className="text-left p-4">Business</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Type</th><th className="text-right p-4">Amount</th><th className="text-right p-4">Points</th><th className="text-right p-4">Actions</th></tr></thead>
          <tbody>
            {!list && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {list && list.map((t) => (
              <tr key={t.id} data-testid="admin-txn-row" className="border-b border-black/5">
                <td className="p-4">{t.business_name}</td>
                <td className="p-4">{t.customer_name}</td>
                <td className="p-4 capitalize">{t.type}</td>
                <td className="p-4 text-right">{t.purchase_amount ? tzs(t.purchase_amount) : "—"}</td>
                <td className={`p-4 text-right font-mono ${t.type === "redemption" ? "text-destructive" : "text-primary"}`}>{t.type === "redemption" ? `-${pts(t.points_spent)}` : `${t.points_awarded >= 0 ? "+" : ""}${pts(t.points_awarded)}`}</td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  <button data-testid="view-audit" onClick={() => showAudit(t)} className="text-xs text-primary inline-flex items-center gap-1"><ClockCounterClockwise size={14} /> Audit</button>
                  <button data-testid="open-adjust" onClick={() => setAdj(t)} className="text-xs text-amber-600">Adjust</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} total={total} pageSize={PAGE} onPage={setPage} />

      <Dialog open={!!audit} onOpenChange={(o) => !o && setAudit(null)}>
        <DialogContent className="bg-card border-black/10">
          <DialogHeader><DialogTitle className="font-display">Audit trail</DialogTitle><DialogDescription>Append-only history of actions on this transaction.</DialogDescription></DialogHeader>
          {audit && (audit.logs.length === 0 ? <p className="text-sm text-muted-foreground">No audit records.</p> : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {audit.logs.map((l) => (
                <div key={l.id} className="text-sm border-b border-black/5 py-2">
                  <p className="font-medium">{l.action}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(l.created_at)} {l.ip ? `· ${l.ip}` : ""}</p>
                  {l.new_state && <pre className="text-[10px] text-muted-foreground mt-1 whitespace-pre-wrap font-mono">{JSON.stringify(l.new_state)}</pre>}
                </div>
              ))}
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={!!adj} onOpenChange={(o) => !o && setAdj(null)}>
        <DialogContent className="bg-card border-black/10">
          <DialogHeader><DialogTitle className="font-display">Create adjustment</DialogTitle><DialogDescription>Add a correcting ledger entry — original history is never edited.</DialogDescription></DialogHeader>
          <p className="text-xs text-muted-foreground">Generates a new ledger entry — history is never edited.</p>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Point delta (+/-)</Label><Input data-testid="adj-delta" type="number" value={adjForm.delta} onChange={(e) => setAdjForm({ ...adjForm, delta: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
            <div><Label className="text-xs text-muted-foreground">Reason</Label><Input data-testid="adj-reason" value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
            <Button data-testid="submit-adjustment" onClick={submitAdj} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Apply adjustment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
